import {
  Injectable,
  ComponentFactoryResolver,
  Injector,
  ApplicationRef,
  EmbeddedViewRef
} from '@angular/core';

@Injectable()
export class DynamicComponentLoaderService {
  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef
  ) {}

  /**
   * Instantiates a dynamic Angular component and attaches it to the DOM element whose id matches `elementId`.
   * Optionally applies the provided `componentInputs` to the component instance before change detection runs.
   *
   * @param elementId The DOM id of the placeholder element that will host the dynamic component.
   * @param dynamicComponent The component class to instantiate.
   * @param componentInputs Optional property bag of @Input values to assign to the dynamic component.
   */
  public attachDynamicComponent(
    elementId: string,
    dynamicComponent: any,
    componentInputs?: { [key: string]: any }
  ) {
    // Find the native element by ID
    const element = document.getElementById(elementId);
    if (!element) {
      console.warn(`[DynamicComponentLoader] Host element '${elementId}' not found`);
      return;
    }

    if (!dynamicComponent) {
      console.error(`[DynamicComponentLoader] No component provided for element '${elementId}'`);
      return;
    }

    console.info('[DynamicComponentLoader] Resolving component for', elementId, dynamicComponent && dynamicComponent.name);

    // Create the component factory
    let componentFactory;
    try {
      componentFactory = this.componentFactoryResolver.resolveComponentFactory(dynamicComponent);
    } catch (error) {
      console.error(`[DynamicComponentLoader] Failed to resolve factory for element '${elementId}'`, dynamicComponent, error);
      throw error;
    }
    
    // Create the component and attach it to the application
    const componentRef = componentFactory.create(this.injector);

    if (componentInputs && typeof componentInputs === 'object' && componentInputs !== null) {
      Object.keys(componentInputs).forEach((inputKey) => {
        componentRef.instance[inputKey] = componentInputs[inputKey];
      });
    }

    this.appRef.attachView(componentRef.hostView);

    // Get the DOM element from the component
    const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

    // Append it to the element
    element.appendChild(domElem);

    if (componentRef.changeDetectorRef && (typeof componentRef.changeDetectorRef.detectChanges === 'function')) {
      componentRef.changeDetectorRef.detectChanges();
    }

    return componentRef;
  }
}
